import { getAuth, getIdToken } from "firebase/auth";
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  doc,
  onSnapshot,
  FirestoreError,
  limit,
  startAfter,
  DocumentSnapshot,
  Timestamp,
} from "firebase/firestore";
import axios from "axios";
import {
  ExistingLoyaltyProgramTemplate,
  LoyaltyProgramTemplateManager,
  APICreateLoyaltyProgramRequest,
  APIUpdateLoyaltyProgramRequest,
  APIDeleteLoyaltyProgramRequest,
} from "../models/loyalty-program-template";
import {
  ExistingLoyaltyTransaction,
  LoyaltyTransactionManager,
  TransactionStatus,
  APIApproveLoyaltyTransactionRequest,
  APIRejectLoyaltyTransactionRequest,
  APIRecalculateLoyaltyRequest,
  APIRecalculateLoyaltyResponse,
} from "../models/loyalty-transaction";
import { logger } from "../logging";

const projectName = import.meta.env.VITE_APP_FIREBASE_PROJECT;
let apiBaseURL = `https://us-central1-${projectName}.cloudfunctions.net/api`;
if (import.meta.env.VITE_APP_USE_FUNCTIONS_EMULATOR === "true") {
  apiBaseURL = `http://localhost:5001/${projectName}/us-central1/api`;
}

// #region Loyalty Program Functions

/**
 * Create a new loyalty program template
 */
export async function createLoyaltyProgram(
  programDoc: APICreateLoyaltyProgramRequest,
): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = "loyalty-programs/create";
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  try {
    await axios.post(fullApiRoute, programDoc, {
      headers: { authorization: `Bearer ${idToken}` },
    });
  } catch (err) {
    if (axios.isAxiosError(err)) {
      logger.info(err.response?.data);
      logger.info(err.response?.status);
      logger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

/**
 * Update an existing loyalty program template
 */
export async function updateLoyaltyProgram(
  partialProgram: APIUpdateLoyaltyProgramRequest,
): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = "loyalty-programs/update";
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  try {
    await axios.post(fullApiRoute, partialProgram, {
      headers: { authorization: `Bearer ${idToken}` },
    });
  } catch (err) {
    if (axios.isAxiosError(err)) {
      logger.info(err.response?.data);
      logger.info(err.response?.status);
      logger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

/**
 * Delete a loyalty program template
 */
export async function deleteLoyaltyProgram(
  deleteRequest: APIDeleteLoyaltyProgramRequest,
): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = "loyalty-programs/delete";
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  try {
    await axios.delete(fullApiRoute, {
      headers: { authorization: `Bearer ${idToken}` },
      data: deleteRequest,
    });
  } catch (err) {
    if (axios.isAxiosError(err)) {
      logger.info(err.response?.data);
      logger.info(err.response?.status);
      logger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

/**
 * Get all loyalty programs for a site
 */
export async function getAllLoyaltyPrograms(
  siteKey: string,
): Promise<ExistingLoyaltyProgramTemplate[]> {
  const db = getFirestore();
  const programsRef = collection(db, "siteKeys", siteKey, "loyaltyPrograms");
  const q = query(programsRef, orderBy("timestampCreated", "desc"));

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) =>
    LoyaltyProgramTemplateManager.createFromSnapshot(doc),
  );
}

/**
 * Get a single loyalty program by ID
 */
export async function getLoyaltyProgram(
  siteKey: string,
  programID: string,
): Promise<ExistingLoyaltyProgramTemplate> {
  const db = getFirestore();
  const programDoc = await getDoc(
    doc(db, "siteKeys", siteKey, "loyaltyPrograms", programID),
  );

  if (!programDoc.exists()) {
    throw new Error(`Loyalty program not found: ${programID}`);
  }

  return LoyaltyProgramTemplateManager.createFromSnapshot(programDoc);
}

/**
 * Subscribe to loyalty programs changes
 */
export function subscribeLoyaltyPrograms(args: {
  siteKey: string;
  onChange: (programs: ExistingLoyaltyProgramTemplate[]) => void;
  onError?: (error: FirestoreError) => void;
}): () => void {
  const db = getFirestore();
  const programsRef = collection(
    db,
    "siteKeys",
    args.siteKey,
    "loyaltyPrograms",
  );
  const q = query(programsRef, orderBy("timestampCreated", "desc"));

  return onSnapshot(
    q,
    (querySnapshot) => {
      const programs = querySnapshot.docs.map((doc) =>
        LoyaltyProgramTemplateManager.createFromSnapshot(doc),
      );
      args.onChange(programs);
    },
    args.onError,
  );
}

/**
 * Get loyalty programs with pagination
 */
export async function getLoyaltyProgramsPaginated(args: {
  siteKey: string;
  pageSize: number;
  lastDoc?: DocumentSnapshot;
  isActive?: boolean;
}): Promise<{
  programs: ExistingLoyaltyProgramTemplate[];
  lastDoc: DocumentSnapshot | null;
  hasMore: boolean;
}> {
  const db = getFirestore();
  const programsRef = collection(
    db,
    "siteKeys",
    args.siteKey,
    "loyaltyPrograms",
  );

  let q = query(
    programsRef,
    orderBy("timestampCreated", "desc"),
    limit(args.pageSize),
  );

  if (args.isActive !== undefined) {
    q = query(
      programsRef,
      where("isActive", "==", args.isActive),
      orderBy("timestampCreated", "desc"),
      limit(args.pageSize),
    );
  }

  if (args.lastDoc) {
    q = query(q, startAfter(args.lastDoc));
  }

  const querySnapshot = await getDocs(q);
  const programs = querySnapshot.docs.map((doc) =>
    LoyaltyProgramTemplateManager.createFromSnapshot(doc),
  );

  return {
    programs,
    lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1] || null,
    hasMore: querySnapshot.docs.length === args.pageSize,
  };
}

// #endregion

// #region Loyalty Transaction Functions

/**
 * Get a single loyalty transaction by ID
 */
export async function getLoyaltyTransaction(
  siteKey: string,
  transactionID: string,
): Promise<ExistingLoyaltyTransaction> {
  const db = getFirestore();
  const transactionDoc = await getDoc(
    doc(db, "siteKeys", siteKey, "loyaltyTransactions", transactionID),
  );

  if (!transactionDoc.exists()) {
    throw new Error(`Loyalty transaction not found: ${transactionID}`);
  }

  return LoyaltyTransactionManager.createFromSnapshot(transactionDoc);
}

/**
 * Get loyalty transactions with pagination and filters
 */
export async function getLoyaltyTransactionsPaginated(args: {
  siteKey: string;
  pageSize: number;
  lastDoc?: DocumentSnapshot;
  status?: TransactionStatus;
  loyaltyProgramID?: string;
  customerID?: string;
}): Promise<{
  transactions: ExistingLoyaltyTransaction[];
  lastDoc: DocumentSnapshot | null;
  hasMore: boolean;
}> {
  const db = getFirestore();
  const transactionsRef = collection(
    db,
    "siteKeys",
    args.siteKey,
    "loyaltyTransactions",
  );

  let q = query(
    transactionsRef,
    orderBy("timestampCreated", "desc"),
    limit(args.pageSize),
  );

  if (args.status) {
    q = query(
      transactionsRef,
      where("status", "==", args.status),
      orderBy("timestampCreated", "desc"),
      limit(args.pageSize),
    );
  }

  if (args.loyaltyProgramID) {
    q = query(
      transactionsRef,
      where("loyaltyProgramID", "==", args.loyaltyProgramID),
      orderBy("timestampCreated", "desc"),
      limit(args.pageSize),
    );
  }

  if (args.customerID) {
    q = query(
      transactionsRef,
      where("customerID", "==", args.customerID),
      orderBy("timestampCreated", "desc"),
      limit(args.pageSize),
    );
  }

  if (args.lastDoc) {
    q = query(q, startAfter(args.lastDoc));
  }

  const querySnapshot = await getDocs(q);
  const transactions = querySnapshot.docs.map((doc) =>
    LoyaltyTransactionManager.createFromSnapshot(doc),
  );

  return {
    transactions,
    lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1] || null,
    hasMore: querySnapshot.docs.length === args.pageSize,
  };
}

/**
 * Get loyalty transactions by status
 */
export async function getLoyaltyTransactionsByStatus(
  siteKey: string,
  status: TransactionStatus,
): Promise<ExistingLoyaltyTransaction[]> {
  const db = getFirestore();
  const transactionsRef = collection(
    db,
    "siteKeys",
    siteKey,
    "loyaltyTransactions",
  );
  const q = query(
    transactionsRef,
    where("status", "==", status),
    orderBy("timestampCreated", "desc"),
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) =>
    LoyaltyTransactionManager.createFromSnapshot(doc),
  );
}

/**
 * Get loyalty transactions by customer ID
 */
export async function getLoyaltyTransactionsByCustomer(
  siteKey: string,
  customerID: string,
): Promise<ExistingLoyaltyTransaction[]> {
  const db = getFirestore();
  const transactionsRef = collection(
    db,
    "siteKeys",
    siteKey,
    "loyaltyTransactions",
  );
  const q = query(
    transactionsRef,
    where("customerID", "==", customerID),
    orderBy("timestampCreated", "desc"),
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) =>
    LoyaltyTransactionManager.createFromSnapshot(doc),
  );
}

export async function getLoyaltyTransactionsByInvoiceID(
  siteKey: string,
  invoiceID: string,
): Promise<ExistingLoyaltyTransaction[]> {
  const db = getFirestore();
  const transactionsRef = collection(
    db,
    "siteKeys",
    siteKey,
    "loyaltyTransactions",
  );
  const q = query(
    transactionsRef,
    where("sourceInvoiceID", "==", invoiceID),
    orderBy("timestampCreated", "desc"),
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) =>
    LoyaltyTransactionManager.createFromSnapshot(doc),
  );
}

/**
 * Get loyalty transactions by craft record ID
 */
export async function getLoyaltyTransactionsByCraftRecordID(
  siteKey: string,
  craftRecordID: string,
): Promise<ExistingLoyaltyTransaction[]> {
  const db = getFirestore();
  const transactionsRef = collection(
    db,
    "siteKeys",
    siteKey,
    "loyaltyTransactions",
  );
  const q = query(
    transactionsRef,
    where("sourceCraftRecordID", "==", craftRecordID),
    orderBy("timestampCreated", "desc"),
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) =>
    LoyaltyTransactionManager.createFromSnapshot(doc),
  );
}

/**
 * Subscribe to loyalty transactions by invoice ID
 */
export function subscribeLoyaltyTransactionsByInvoiceID(args: {
  siteKey: string;
  invoiceID: string;
  onChange: (transactions: ExistingLoyaltyTransaction[]) => void;
  onError?: (error: FirestoreError) => void;
}): () => void {
  const db = getFirestore();
  const transactionsRef = collection(
    db,
    "siteKeys",
    args.siteKey,
    "loyaltyTransactions",
  );
  const q = query(
    transactionsRef,
    where("sourceInvoiceID", "==", args.invoiceID),
    orderBy("timestampCreated", "desc"),
  );

  return onSnapshot(
    q,
    (querySnapshot) => {
      const transactions = querySnapshot.docs.map((doc) =>
        LoyaltyTransactionManager.createFromSnapshot(doc),
      );
      args.onChange(transactions);
    },
    args.onError,
  );
}

/**
 * Subscribe to loyalty transactions by craft record ID
 */
export function subscribeLoyaltyTransactionsByCraftRecordID(args: {
  siteKey: string;
  craftRecordID: string;
  onChange: (transactions: ExistingLoyaltyTransaction[]) => void;
  onError?: (error: FirestoreError) => void;
}): () => void {
  const db = getFirestore();
  const transactionsRef = collection(
    db,
    "siteKeys",
    args.siteKey,
    "loyaltyTransactions",
  );
  const q = query(
    transactionsRef,
    where("sourceCraftRecordID", "==", args.craftRecordID),
    orderBy("timestampCreated", "desc"),
  );

  return onSnapshot(
    q,
    (querySnapshot) => {
      const transactions = querySnapshot.docs.map((doc) =>
        LoyaltyTransactionManager.createFromSnapshot(doc),
      );
      args.onChange(transactions);
    },
    args.onError,
  );
}

/**
 * Approve a loyalty transaction
 */
export async function approveLoyaltyTransaction(
  approveRequest: APIApproveLoyaltyTransactionRequest,
): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = "loyalty-transactions/approve";
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  try {
    await axios.post(fullApiRoute, approveRequest, {
      headers: { authorization: `Bearer ${idToken}` },
    });
  } catch (err) {
    if (axios.isAxiosError(err)) {
      logger.info(err.response?.data);
      logger.info(err.response?.status);
      logger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

/**
 * Reject a loyalty transaction
 */
export async function rejectLoyaltyTransaction(
  rejectRequest: APIRejectLoyaltyTransactionRequest,
): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = "loyalty-transactions/reject";
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  try {
    await axios.post(fullApiRoute, rejectRequest, {
      headers: { authorization: `Bearer ${idToken}` },
    });
  } catch (err) {
    if (axios.isAxiosError(err)) {
      logger.info(err.response?.data);
      logger.info(err.response?.status);
      logger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

/**
 * Subscribe to loyalty transactions with date range filtering
 */
export function subscribeLoyaltyTransactionsWithDateRange(args: {
  siteKey: string;
  startDate: Timestamp;
  endDate: Timestamp;
  status?: TransactionStatus;
  onChange: (transactions: ExistingLoyaltyTransaction[]) => void;
  onError?: (error: FirestoreError) => void;
}): () => void {
  const db = getFirestore();
  const transactionsRef = collection(
    db,
    "siteKeys",
    args.siteKey,
    "loyaltyTransactions",
  );

  let q = query(
    transactionsRef,
    where("earnedDate", ">=", args.startDate),
    where("earnedDate", "<=", args.endDate),
    orderBy("earnedDate", "desc"),
  );

  // Add status filter if provided
  if (args.status) {
    q = query(
      transactionsRef,
      where("status", "==", args.status),
      where("earnedDate", ">=", args.startDate),
      where("earnedDate", "<=", args.endDate),
      orderBy("earnedDate", "desc"),
    );
  }

  return onSnapshot(
    q,
    (querySnapshot) => {
      const transactions = querySnapshot.docs.map((doc) =>
        LoyaltyTransactionManager.createFromSnapshot(doc),
      );
      args.onChange(transactions);
    },
    args.onError,
  );
}

/**
 * Subscribe to pending loyalty transactions
 */
export function subscribePendingLoyaltyTransactions(args: {
  siteKey: string;
  onChange: (transactions: ExistingLoyaltyTransaction[]) => void;
  onError?: (error: FirestoreError) => void;
}): () => void {
  const db = getFirestore();
  const transactionsRef = collection(
    db,
    "siteKeys",
    args.siteKey,
    "loyaltyTransactions",
  );
  const q = query(
    transactionsRef,
    where("status", "==", TransactionStatus.PENDING),
    orderBy("timestampCreated", "desc"),
  );

  return onSnapshot(
    q,
    (querySnapshot) => {
      const transactions = querySnapshot.docs.map((doc) =>
        LoyaltyTransactionManager.createFromSnapshot(doc),
      );
      args.onChange(transactions);
    },
    args.onError,
  );
}

/**
 * Recalculate loyalty transactions for an invoice or craft record
 */
export async function recalculateLoyalty(
  request: APIRecalculateLoyaltyRequest,
): Promise<APIRecalculateLoyaltyResponse> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = "loyalty-transactions/recalculate";
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  try {
    const response = await axios.post(fullApiRoute, request, {
      headers: { authorization: `Bearer ${idToken}` },
    });
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      logger.info(err.response?.data);
      logger.info(err.response?.status);
      logger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

// #endregion
